import React, { Component, createRef } from 'react';
import {
  withRouter,
  Redirect,
} from 'react-router-dom';
import { connect as reduxConnect } from 'react-redux';
import PropTypes from 'prop-types';

import { validateEmail } from '../../validators';
import processBadResponse from '../../processBadResponse';
import { setAuthError } from '../../okapiActions';
import { defaultErrors } from '../../constants';

import { ALTCHA_HEADER } from '../Recek/altcha/AltchaWidget.tsx';
import ForgotPasswordForm from './ForgotPasswordForm';

export const COMPLETE_REGISTRATION_VARIANT = 'completeRegistration';

/** Anonymous mod-br-users captcha challenge of this form (DB-876). */
export const FORGOTTEN_PASSWORD_CAPTCHA_PATH = 'br-users/captcha/challenge';

// ReČeK stores its users in mod-br-users, so the self-service reset must target br-users rather
// than the FOLIO bl-users / users-keycloak modules (which do not know these users).
const FORGOTTEN_PASSWORD_PATH = 'br-users/forgotten/password';

class ForgotPasswordCtrl extends Component {
  static propTypes = {
    authFailure: PropTypes.arrayOf(PropTypes.object),
    location: PropTypes.shape({
      search: PropTypes.string,
    }),
    // 'completeRegistration' renders the same reset request as "Dokončení registrace" with the
    // email prefilled from ?email= (DB-736); undefined keeps the plain forgotten-password page.
    variant: PropTypes.oneOf([COMPLETE_REGISTRATION_VARIANT]),
    stripes: PropTypes.shape({
      okapi: PropTypes.shape({
        url: PropTypes.string.isRequired,
        tenant: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    handleBadResponse: PropTypes.func.isRequired,
    setAuthErrors: PropTypes.func.isRequired,
    clearAuthErrors: PropTypes.func.isRequired,
  };

  static defaultProps = {
    authFailure: [],
    location: { search: '' },
    variant: undefined,
  };

  constructor(props) {
    super(props);

    this.state = {
      isValidEmail: true,
      userExists: false,
      userEmail: '',
    };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.captchaRef = createRef();
    // The solved captcha is single-use, so it lives outside React state: it is read once per submit.
    this.captchaPayload = null;
  }

  componentWillUnmount() {
    this.props.clearAuthErrors();
  }

  handleCaptchaPayload = (payload) => {
    this.captchaPayload = payload;
  };

  /**
   * Asks mod-br-users to send the reset link. The request carries the solved captcha in the
   * X-Altcha header (DB-876), which a stripes-connect resource cannot add per request.
   */
  requestPasswordReset = (email, captchaPayload) => {
    const { okapi: { url, tenant } } = this.props.stripes;

    return fetch(`${url}/${FORGOTTEN_PASSWORD_PATH}`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        // the endpoint answers with an empty body and no Content-Type
        'accept': '*/*',
        'Content-Type': 'application/json',
        'X-Okapi-Tenant': tenant,
        [ALTCHA_HEADER]: captchaPayload,
      },
      body: JSON.stringify({ id: email }),
    });
  };

  async handleSubmit(values) {
    this.resetState();
    const { handleBadResponse, setAuthErrors } = this.props;
    const { userInput } = values;
    const { FORGOTTEN_PASSWORD_CLIENT_ERROR, CAPTCHA_REQUIRED_ERROR } = defaultErrors;
    const normalizedInput = String(userInput).trim();
    const isValidInput = validateEmail(normalizedInput);

    if (!isValidInput) {
      this.setState({ isValidEmail: false });
      return;
    }

    const captchaPayload = this.captchaPayload;
    if (!captchaPayload) {
      setAuthErrors([CAPTCHA_REQUIRED_ERROR]);
      return;
    }

    let response;
    try {
      response = await this.requestPasswordReset(normalizedInput, captchaPayload);
    } catch (error) {
      this.resetCaptcha();
      await handleBadResponse(error, FORGOTTEN_PASSWORD_CLIENT_ERROR);
      return;
    }

    if (response.ok) {
      this.handleSuccessfulResponse(normalizedInput);
      return;
    }
    this.resetCaptcha();
    await handleBadResponse(response, FORGOTTEN_PASSWORD_CLIENT_ERROR);
  }

  /** The server consumed the solution (or rejected it): the user has to verify again. */
  resetCaptcha = () => {
    this.captchaPayload = null;
    this.captchaRef.current?.reset();
  };

  getInitialEmail = () => {
    const { location, variant } = this.props;
    if (variant !== COMPLETE_REGISTRATION_VARIANT) {
      return '';
    }
    return new URLSearchParams(location?.search || '').get('email') || '';
  };

  handleSuccessfulResponse = (userEmail) => {
    this.setState({
      userExists: true,
      userEmail,
    });
  };

  resetState = () => {
    this.setState({
      isValidEmail: true,
      userExists: false,
    });
  };

  render() {
    const { authFailure, variant } = this.props;

    const {
      userExists,
      isValidEmail,
      userEmail,
    } = this.state;

    if (userExists) {
      const state = variant ? { userEmail, flow: variant } : { userEmail };
      return <Redirect to={{
        pathname: '/check-email',
        state,
      }}
      />;
    }

    return (
      <ForgotPasswordForm
        isValid={isValidEmail}
        errors={authFailure}
        onSubmit={this.handleSubmit}
        variant={variant}
        initialEmail={this.getInitialEmail()}
        captchaRef={this.captchaRef}
        captchaChallengePath={FORGOTTEN_PASSWORD_CAPTCHA_PATH}
        onCaptchaPayload={this.handleCaptchaPayload}
      />
    );
  }
}

const mapStateToProps = state => ({ authFailure: state.okapi.authFailure });
const mapDispatchToProps = dispatch => ({
  handleBadResponse: (error, defaultClientError) => processBadResponse(dispatch, error, defaultClientError),
  setAuthErrors: (errors) => dispatch(setAuthError(errors)),
  clearAuthErrors: () => dispatch(setAuthError([])),
});

export default withRouter(reduxConnect(mapStateToProps, mapDispatchToProps)(ForgotPasswordCtrl));
